"use client";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@aitisal/ui/retail-storefront/accordion";
import { Button } from "@aitisal/ui/retail-storefront/button";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
  DrawerTrigger,
} from "@aitisal/ui/retail-storefront/drawer";
import { useNavigate } from "@tanstack/react-router";
import { ChevronRight, Heart, Menu, Package, X } from "lucide-react";
import React, { useState } from "react";
import { getTitle } from "../../helper/helper";
import { useBrandingConfig } from "../../utils/brandingConfig";
import StoreLogo from "./StoreLogo";

interface MobileSidebarProps {
  sections: any[];
}

const MobileSidebar: React.FC<MobileSidebarProps> = ({ sections }) => {
  const navigator = useNavigate();
  const [open, setOpen] = useState(false);

  const brandingConfig = useBrandingConfig();
  const logoUrl = brandingConfig.logo || "";

  const defaultCategories = [
    {
      id: "1",
      name: "Mens",
      subcategories: ["Formal wear", "Casual wear", "Active wear", "Foot wear"],
    },
    {
      id: "2",
      name: "Womens",
      subcategories: ["Formal Wear", "Casual Wear", "Active Wear", "Foot Wear"],
    },
    {
      id: "3",
      name: "Kids",
      subcategories: [
        "Girls Clothing",
        "Boys Clothing",
        "Girls Footwear",
        "Boys Footwear",
      ],
    },
    { id: "4", name: "Wellness" },
    { id: "5", name: "Bookings" },
    { id: "6", name: "Household" },
    { id: "7", name: "Electronics" },
    { id: "8", name: "Books & Stationery" },
  ];

  const categoriesToShow =
    sections.length > 0
      ? sections.map((section: any) => ({
          id: section.sectionId,
          name: getTitle(section?.name || ""),
          subcategories: section.subcategories,
        }))
      : defaultCategories;

  const handleQuickLink = (path: string) => {
    setOpen(false);
    navigator({ to: path });
  };

  return (
    <Drawer direction="left" open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="rounded-md hover:bg-gray-100 active:bg-gray-200 transition-colors cursor-pointer"
          aria-label="Open mobile navigation menu"
        >
          <Menu className="h-9 w-9 text-gray-700" strokeWidth={2.5} />
        </Button>
      </DrawerTrigger>

      <DrawerContent className="z-[1000] inset-x-auto bottom-auto left-0 top-0 mt-0 h-full w-full sm:w-[300px] rounded-none border-r border-gray-100 bg-gray-50 text-gray-900 [&>div.mx-auto]:hidden outline-none">
        <div className="flex flex-col h-full w-full">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between bg-white shadow-sm">
            <div className="flex items-center gap-3 min-h-[64px] min-w-0">
              <StoreLogo
                src={logoUrl}
                title={brandingConfig.title}
                className="h-12 w-auto max-w-[140px] rounded-lg"
              />
              {brandingConfig.title && (
                <span className="truncate text-xl font-bold text-gray-800 leading-none">
                  {brandingConfig.title}
                </span>
              )}
              <DrawerTitle className="sr-only">Navigation menu</DrawerTitle>
            </div>

            <DrawerClose asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-full hover:bg-gray-100 text-gray-500"
              >
                <X className="h-4 w-4" />
              </Button>
            </DrawerClose>
          </div>

          {/* Categories Navigation */}
          <div className="flex-1 overflow-y-auto px-3 py-2">
            <DrawerDescription className="sr-only">
              Navigate through categories
            </DrawerDescription>

            <div className="space-y-1">
              <p className="px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Shop Categories
              </p>
              {categoriesToShow.map((category: any) =>
                category.subcategories && category.subcategories.length > 0 ? (
                  <Accordion
                    key={category.id}
                    type="single"
                    collapsible
                    className="w-full"
                  >
                    <AccordionItem
                      value={category.name}
                      className="border-none"
                    >
                      <AccordionTrigger className="w-full px-3 py-2.5 text-sm font-medium text-gray-600 hover:bg-white hover:text-gray-900 hover:no-underline rounded-lg transition-all [&>svg]:w-4 [&>svg]:h-4">
                        <span>{category.name}</span>
                      </AccordionTrigger>
                      <AccordionContent className="mt-1 ml-3 border-l-2 border-gray-200 pl-3 pb-1">
                        <div className="space-y-0.5">
                          {category.subcategories.map(
                            (sub: string, idx: number) => (
                              <Button
                                variant="ghost"
                                key={idx}
                                onClick={() => {
                                  setOpen(false);
                                  navigator({
                                    to: "/c/shop",
                                    search: {
                                      category: sub,
                                      search: undefined,
                                    },
                                  });
                                }}
                                className="h-auto w-full justify-start text-left px-3 py-2 text-sm text-gray-500 hover:bg-white hover:text-gray-900 rounded-lg transition-colors"
                              >
                                {sub}
                              </Button>
                            ),
                          )}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                ) : (
                  <div key={category.id}>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setOpen(false);
                        navigator({
                          to: "/c/shop",
                          search: {
                            category: category.name,
                            search: undefined,
                          },
                        });
                      }}
                      className="h-auto w-full justify-between px-3 py-2.5 text-sm font-medium text-gray-600 hover:bg-white hover:text-gray-900 rounded-lg transition-all"
                    >
                      <span>{category.name}</span>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                ),
              )}
            </div>
          </div>

          {/* Quick Links Footer */}
          <div className="p-3 border-t border-gray-200 bg-white space-y-1">
            <p className="px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
              Quick Links
            </p>
            <Button
              variant="ghost"
              onClick={() => handleQuickLink("/c/wishlist")}
              className="h-auto w-full justify-start gap-3 px-3 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Heart className="w-4 h-4" />
              My Wishlist
            </Button>
            <Button
              variant="ghost"
              onClick={() => handleQuickLink("/c/ordertracking")}
              className="h-auto w-full justify-start gap-3 px-3 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Package className="w-4 h-4" />
              Track Order
            </Button>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default MobileSidebar;
